import { useEffect, useMemo, useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { ChevronDown, Loader2, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  useGetMachineryDirectiveCompliance,
  useListHazardRiskReductionSteps,
  useListRiskReductionMeasures,
  useSuggestApplicableRiskReductionMeasures,
  type ApplicableRiskReductionMeasureRow,
  type RiskOutFitAssessment,
  type RiskOutGenerationMode,
  type RiskReductionMeasureType,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { CreateHazardRiskReductionStepFormValues } from './CreateHazardRiskReductionStepForm.schema'
import {
  RiskInjurySelect,
  RiskFrequencySelect,
  RiskAvoidanceSelect,
  RiskLikelihoodSelect,
} from './RiskDimensionSelects'
import {
  buildApplicableReasonMap,
  filterMeasuresForPickerMode,
  groupMeasuresForPicker,
  resolveMeasurePickerMode,
  RRM_HIERARCHY_TYPE_ORDER,
  type MeasurePickerMode,
} from './applicableRiskReductionMeasurePicker'
import {
  getRrmHierarchyTypesWarning,
  type RrmHierarchyTypesWarning,
} from './rrmHierarchyTypes'
import {
  listRiskOutDimensionsAboveCap,
  RISK_OUT_DIMENSION_LABEL_KEYS,
} from './riskOutDimensionCap'
import { useHazardRiskReductionStepRiskInCap } from './useHazardRiskReductionStepRiskInCap'

export type HazardRiskReductionStepFormFieldsProps = {
  form: UseFormReturn<CreateHazardRiskReductionStepFormValues>
  hazardId: string
  complianceId: string
  excludeStepId?: string
  isGenerating: boolean
  hasAllRiskOutDimensionsSelected: boolean
  onGenerateRiskOut: (mode: RiskOutGenerationMode) => void
  fitAssessment?: RiskOutFitAssessment | null
  onClearFitAssessment?: () => void
}

function getHierarchyWarningMessageKey(
  warning: RrmHierarchyTypesWarning
): string {
  if (warning === 'skippedIsdAndScp') {
    return 'hazardsPage.hazardDetail.riskFlow.hierarchyWarningSkippedIsdAndScp'
  }
  if (warning === 'isdAfterLater') {
    return 'hazardsPage.hazardDetail.riskFlow.hierarchyWarningIsdAfterLater'
  }
  return 'hazardsPage.hazardDetail.riskFlow.hierarchyWarningScpAfterResidual'
}

function WriteJustificationsMenuItem({
  disabled,
  disabledTooltip,
  label,
  onSelect,
}: {
  disabled: boolean
  disabledTooltip: string
  label: string
  onSelect: () => void
}) {
  const menuItem = (
    <DropdownMenuItem disabled={disabled} onClick={onSelect}>
      {label}
    </DropdownMenuItem>
  )

  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className='w-full cursor-not-allowed'>{menuItem}</div>
        </TooltipTrigger>
        <TooltipContent side='right'>{disabledTooltip}</TooltipContent>
      </Tooltip>
    )
  }

  return menuItem
}

function ApplicableMeasureSelectLabel({
  name,
  type,
  reason,
  isUsed,
  isSuggested,
}: {
  name: string
  type: string
  reason?: string
  isUsed?: boolean
  isSuggested?: boolean
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const label = `${name} (${type})`
  const trimmedReason = reason?.trim()
  const showUsedBadge = isUsed === true
  const showSuggestedBadge = isSuggested === true
  const showWhyLink = trimmedReason != null && trimmedReason.length > 0

  if (!showWhyLink && !showUsedBadge && !showSuggestedBadge) {
    return <>{label}</>
  }

  return (
    <div className='flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1'>
      <span className='min-w-0 wrap-break-word'>{label}</span>
      {showSuggestedBadge && (
        <Badge variant='secondary' className='shrink-0 py-0 text-xs font-normal'>
          {t('hazardsPage.hazardDetail.riskFlow.suggestedMeasureBadge')}
        </Badge>
      )}
      {showUsedBadge && (
        <Badge variant='warning' className='shrink-0 py-0 text-xs font-normal'>
          {t('hazardsPage.hazardDetail.riskFlow.usedMeasureBadge')}
        </Badge>
      )}
      {showWhyLink && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type='button'
              className='inline-flex cursor-default rounded border-0 bg-transparent p-0 text-xs text-primary underline-offset-4 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
              aria-label={t(
                'hazardsPage.hazardDetail.riskFlow.applicableMeasureWhyAriaLabel',
                { name }
              )}
              onPointerDown={(event) => {
                event.stopPropagation()
              }}
              onClick={(event) => {
                event.stopPropagation()
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  event.stopPropagation()
                }
              }}
            >
              {t('hazardsPage.hazardDetail.riskFlow.applicableMeasureWhyLink')}
            </button>
          </TooltipTrigger>
          <TooltipContent side='top' className='max-w-md text-left'>
            {trimmedReason}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

export function HazardRiskReductionStepFormFields({
  form,
  hazardId,
  complianceId,
  excludeStepId,
  isGenerating,
  hasAllRiskOutDimensionsSelected,
  onGenerateRiskOut,
  fitAssessment,
  onClearFitAssessment,
}: HazardRiskReductionStepFormFieldsProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const { data: compliance } = useGetMachineryDirectiveCompliance(complianceId)
  const { data: riskReductionMeasures = [] } =
    useListRiskReductionMeasures(complianceId)
  const { data: hazardSteps = [] } = useListHazardRiskReductionSteps(hazardId)
  const suggestApplicableMeasures = useSuggestApplicableRiskReductionMeasures()
  const [hasSuggestionResult, setHasSuggestionResult] = useState(false)
  const [showSuggestedOnly, setShowSuggestedOnly] = useState(false)
  const [applicableRows, setApplicableRows] = useState<
    ApplicableRiskReductionMeasureRow[]
  >([])

  const { riskInCap, isRiskOutCapPending } =
    useHazardRiskReductionStepRiskInCap({
      hazardId,
      complianceId,
      excludeStepId,
    })

  const [
    riskOutInjury,
    riskOutFrequency,
    riskOutAvoidance,
    riskOutLikelihood,
  ] = form.watch([
    'riskOutInjury',
    'riskOutFrequency',
    'riskOutAvoidance',
    'riskOutLikelihood',
  ])

  const riskOutDimensionsAboveCap = useMemo(() => {
    if (isRiskOutCapPending) {
      return []
    }
    return listRiskOutDimensionsAboveCap(riskInCap, {
      injury: riskOutInjury,
      frequency: riskOutFrequency,
      avoidance: riskOutAvoidance,
      likelihood: riskOutLikelihood,
    })
  }, [
    isRiskOutCapPending,
    riskInCap,
    riskOutAvoidance,
    riskOutFrequency,
    riskOutInjury,
    riskOutLikelihood,
  ])

  const showRiskOutBlocksSaveAlert = riskOutDimensionsAboveCap.length > 0

  useEffect(() => {
    setHasSuggestionResult(false)
    setShowSuggestedOnly(false)
    setApplicableRows([])
  }, [hazardId])

  const applicableIds = useMemo(
    () => applicableRows.map((row) => row.riskReductionMeasureId),
    [applicableRows]
  )
  const applicableIdSet = useMemo(() => new Set(applicableIds), [applicableIds])
  const applicableReasonMap = useMemo(
    () => buildApplicableReasonMap(applicableRows),
    [applicableRows]
  )

  const measurePickerMode: MeasurePickerMode = resolveMeasurePickerMode({
    hasSuggestionResult,
    showSuggestedOnly,
    applicableIds,
  })

  const visibleMeasures = useMemo(
    () =>
      filterMeasuresForPickerMode(
        riskReductionMeasures,
        measurePickerMode,
        applicableIdSet
      ),
    [applicableIdSet, measurePickerMode, riskReductionMeasures]
  )

  const groupedMeasures = useMemo(
    () =>
      groupMeasuresForPicker(
        visibleMeasures,
        hasSuggestionResult ? applicableIdSet : undefined
      ),
    [applicableIdSet, hasSuggestionResult, visibleMeasures]
  )

  const riskReductionMeasureItems = useMemo(
    () =>
      visibleMeasures.map((rrm) => ({
        value: rrm.id,
        label: `${rrm.name} (${rrm.type})`,
      })),
    [visibleMeasures]
  )

  const selectedRrmId = form.watch('riskReductionMeasureId')

  const existingTypes = useMemo(() => {
    const currentSequence = excludeStepId
      ? hazardSteps.find((step) => step.id === excludeStepId)?.sequence
      : undefined

    return hazardSteps
      .filter((step) => step.id !== excludeStepId)
      .filter((step) =>
        currentSequence != null ? step.sequence < currentSequence : true
      )
      .map((step) => step.riskReductionMeasure.type)
  }, [excludeStepId, hazardSteps])

  const usedMeasureIdSet = useMemo(
    () =>
      new Set(
        hazardSteps
          .filter((step) => step.id !== excludeStepId)
          .map((step) => step.riskReductionMeasureId)
      ),
    [excludeStepId, hazardSteps]
  )

  const selectedMeasure = useMemo(
    () => riskReductionMeasures.find((rrm) => rrm.id === selectedRrmId),
    [riskReductionMeasures, selectedRrmId]
  )

  const selectedType = selectedMeasure?.type

  const hierarchyWarning = getRrmHierarchyTypesWarning(
    selectedType,
    existingTypes
  )

  const showHierarchyWarning = hierarchyWarning != null

  const isSuggesting = suggestApplicableMeasures.isPending
  const isMeasureSelectDisabled = isGenerating || isSuggesting
  const isRiskOutSelectDisabled =
    isGenerating || isRiskOutCapPending || isSuggesting

  const getMeasureTypeLabel = (type: RiskReductionMeasureType) =>
    t(`riskReductionMeasuresLibraryPage.form.measureTypes.${type}`)

  let generatingLabel = t('hazardsPage.hazardDetail.riskFlow.suggestRiskOut')
  if (isGenerating) {
    generatingLabel = t('hazardsPage.hazardDetail.riskFlow.generatingRiskOut')
  }

  const showFitAssessment = fitAssessment != null
  const fitAssessmentTitleKey =
    'hazardsPage.hazardDetail.riskFlow.incompatibleRrmTitle'

  const handleSuggestApplicableMeasures = () => {
    const contentLanguage = compliance?.contentLanguage
    if (!contentLanguage) {
      return
    }

    suggestApplicableMeasures.mutate(
      { hazardId, language: contentLanguage },
      {
        onSuccess: (result) => {
          setApplicableRows(result.measures)
          setHasSuggestionResult(true)
          setShowSuggestedOnly(true)
        },
        onError: (error) => {
          const message =
            error instanceof Error
              ? error.message
              : t(
                  'hazardsPage.hazardDetail.riskFlow.suggestApplicableMeasuresFailed'
                )
          toast.error(message)
        },
      }
    )
  }

  let suggestButtonLabel = t(
    'hazardsPage.hazardDetail.riskFlow.suggestApplicableMeasures'
  )
  if (isSuggesting) {
    suggestButtonLabel = t(
      'hazardsPage.hazardDetail.riskFlow.suggestingApplicableMeasures'
    )
  } else if (hasSuggestionResult) {
    suggestButtonLabel = t(
      'hazardsPage.hazardDetail.riskFlow.refreshSuggestions'
    )
  }

  const canRunSuggestions =
    !isSuggesting && !isGenerating && Boolean(compliance?.contentLanguage)
  const isSuggestionViewDisabled = isSuggesting || isGenerating

  let suggestionViewValue = 'all'
  if (showSuggestedOnly) {
    suggestionViewValue = 'suggested'
  }

  const suggestRiskOutMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type='button'
          variant='outline'
          size='sm'
          disabled={!selectedRrmId || isRiskOutSelectDisabled}
        >
          {isGenerating ? (
            <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
          ) : (
            <Sparkles className='mr-1.5 h-3.5 w-3.5' />
          )}
          {generatingLabel}
          {!isGenerating ? <ChevronDown className='ml-1 h-3.5 w-3.5' /> : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        <DropdownMenuItem onClick={() => onGenerateRiskOut('dimensions')}>
          {t('hazardsPage.hazardDetail.riskFlow.suggestRiskOut')}
        </DropdownMenuItem>
        <WriteJustificationsMenuItem
          disabled={!hasAllRiskOutDimensionsSelected}
          disabledTooltip={t(
            'hazardsPage.hazardDetail.riskFlow.writeJustificationsDisabledTooltip'
          )}
          label={t('hazardsPage.hazardDetail.riskFlow.writeJustifications')}
          onSelect={() => onGenerateRiskOut('justifications')}
        />
        <DropdownMenuItem onClick={() => onGenerateRiskOut('both')}>
          {t('hazardsPage.hazardDetail.riskFlow.doBoth')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <div className='flex flex-col space-y-6'>
      <FormField
        control={form.control}
        name='riskReductionMeasureId'
        render={({ field }) => (
          <FormItem>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <FormLabel>
                {t(
                  'hazardsPage.hazardDetail.riskFlow.selectRiskReductionMeasure'
                )}{' '}
                *
              </FormLabel>
              <div className='flex flex-wrap items-center gap-3'>
                {hasSuggestionResult && (
                  <RadioGroup
                    value={suggestionViewValue}
                    disabled={isSuggestionViewDisabled}
                    aria-label={t(
                      'hazardsPage.hazardDetail.riskFlow.suggestionViewLabel'
                    )}
                    className='flex items-center gap-3'
                    onValueChange={(value) => {
                      if (value == null) {
                        return
                      }
                      setShowSuggestedOnly(value === 'suggested')
                    }}
                  >
                    <div className='flex items-center gap-2'>
                      <RadioGroupItem
                        value='suggested'
                        id='rrm-step-view-suggested'
                      />
                      <Label
                        htmlFor='rrm-step-view-suggested'
                        className='cursor-pointer text-sm font-normal'
                      >
                        {t(
                          'hazardsPage.hazardDetail.riskFlow.showSuggestedOnly'
                        )}
                      </Label>
                    </div>
                    <div className='flex items-center gap-2'>
                      <RadioGroupItem value='all' id='rrm-step-view-all' />
                      <Label
                        htmlFor='rrm-step-view-all'
                        className='cursor-pointer text-sm font-normal'
                      >
                        {t('hazardsPage.hazardDetail.riskFlow.showAllMeasures')}
                      </Label>
                    </div>
                  </RadioGroup>
                )}
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={!canRunSuggestions}
                  onClick={handleSuggestApplicableMeasures}
                >
                  {isSuggesting ? (
                    <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <Sparkles className='mr-1.5 h-3.5 w-3.5' />
                  )}
                  {suggestButtonLabel}
                </Button>
              </div>
            </div>
            <Select
              value={field.value}
              onValueChange={(value) => {
                if (value) {
                  onClearFitAssessment?.()
                  field.onChange(value)
                }
              }}
              items={riskReductionMeasureItems}
            >
              <FormControl>
                <SelectTrigger
                  className='w-full'
                  id='rrm-step-measure'
                  disabled={isMeasureSelectDisabled}
                >
                  <SelectValue
                    placeholder={t(
                      'hazardsPage.hazardDetail.riskFlow.selectRiskReductionMeasurePlaceholder'
                    )}
                  >
                    {selectedMeasure != null ? (
                      <ApplicableMeasureSelectLabel
                        name={selectedMeasure.name}
                        type={selectedMeasure.type}
                        isUsed={usedMeasureIdSet.has(selectedMeasure.id)}
                        isSuggested={
                          hasSuggestionResult &&
                          !showSuggestedOnly &&
                          applicableIdSet.has(selectedMeasure.id)
                        }
                      />
                    ) : null}
                  </SelectValue>
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {measurePickerMode === 'applicable-empty' ? (
                  <p className='px-2 py-3 text-sm text-muted-foreground'>
                    {t(
                      'hazardsPage.hazardDetail.riskFlow.noApplicableMeasures'
                    )}
                  </p>
                ) : (
                  RRM_HIERARCHY_TYPE_ORDER.map((type) => {
                    const measuresForType = groupedMeasures.get(type) ?? []

                    if (measuresForType.length === 0) {
                      return null
                    }

                    return (
                      <SelectGroup key={type}>
                        <SelectLabel className='font-semibold text-foreground'>
                          {getMeasureTypeLabel(type)}
                        </SelectLabel>
                        {measuresForType.map((rrm) => {
                          const reason = applicableReasonMap.get(rrm.id)
                          const isSuggested = applicableIdSet.has(rrm.id)
                          const showReason =
                            hasSuggestionResult &&
                            isSuggested &&
                            reason != null &&
                            reason.length > 0

                          return (
                            <SelectItem
                              key={rrm.id}
                              value={rrm.id}
                              className='items-start'
                            >
                              <ApplicableMeasureSelectLabel
                                name={rrm.name}
                                type={rrm.type}
                                reason={showReason ? reason : undefined}
                                isUsed={usedMeasureIdSet.has(rrm.id)}
                                isSuggested={
                                  hasSuggestionResult &&
                                  !showSuggestedOnly &&
                                  isSuggested
                                }
                              />
                            </SelectItem>
                          )
                        })}
                      </SelectGroup>
                    )
                  })
                )}
              </SelectContent>
            </Select>
            {showHierarchyWarning && hierarchyWarning != null && (
              <Alert variant='warning'>
                <AlertDescription>
                  {t(getHierarchyWarningMessageKey(hierarchyWarning))}
                </AlertDescription>
              </Alert>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      <div className='space-y-3'>
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <p className='text-sm font-medium'>
            {t('hazardsPage.hazardDetail.riskFlow.riskOut')}
          </p>
          {!selectedRrmId ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='inline-flex cursor-not-allowed'>
                  {suggestRiskOutMenu}
                </span>
              </TooltipTrigger>
              <TooltipContent align='end'>
                {t(
                  'hazardsPage.hazardDetail.riskFlow.suggestRiskOutSelectMeasureTooltip'
                )}
              </TooltipContent>
            </Tooltip>
          ) : (
            suggestRiskOutMenu
          )}
        </div>
        {showFitAssessment && fitAssessment != null ? (
          <Alert variant='warning'>
            <AlertTitle>{t(fitAssessmentTitleKey)}</AlertTitle>
            <AlertDescription>{fitAssessment.fitSummary}</AlertDescription>
          </Alert>
        ) : null}
        {showRiskOutBlocksSaveAlert && (
          <Alert variant='destructive'>
            <AlertTitle>
              {t(
                'hazardsPage.hazardDetail.riskFlow.riskOutBlocksSaveTitle'
              )}
            </AlertTitle>
            <AlertDescription>
              <p>
                {t(
                  'hazardsPage.hazardDetail.riskFlow.riskOutBlocksSaveDescription'
                )}
              </p>
              <ul className='mt-2 list-disc pl-5'>
                {riskOutDimensionsAboveCap.map((dimension) => (
                  <li key={dimension}>
                    {t(RISK_OUT_DIMENSION_LABEL_KEYS[dimension])}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          <div className='space-y-3'>
            <FormField
              control={form.control}
              name='riskOutInjury'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('hazardsPage.hazardDetail.riskFlow.injury')}
                  </FormLabel>
                  <FormControl>
                    <RiskInjurySelect
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isRiskOutSelectDisabled}
                      maxAllowedValue={riskInCap.injury}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.select'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='riskOutInjuryJustification'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-xs'>
                    {t(
                      'hazardsPage.hazardDetail.riskFlow.justificationOptional'
                    )}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ''}
                      rows={4}
                      disabled={isGenerating}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.justificationPlaceholder',
                        'Justification...'
                      )}
                      className='resize-none text-xs'
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className='space-y-3'>
            <FormField
              control={form.control}
              name='riskOutFrequency'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('hazardsPage.hazardDetail.riskFlow.frequency')}
                  </FormLabel>
                  <FormControl>
                    <RiskFrequencySelect
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isRiskOutSelectDisabled}
                      maxAllowedValue={riskInCap.frequency}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.select'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='riskOutFrequencyJustification'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-xs'>
                    {t(
                      'hazardsPage.hazardDetail.riskFlow.justificationOptional'
                    )}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ''}
                      rows={4}
                      disabled={isGenerating}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.justificationPlaceholder',
                        'Justification...'
                      )}
                      className='resize-none text-xs'
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className='space-y-3'>
            <FormField
              control={form.control}
              name='riskOutAvoidance'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('hazardsPage.hazardDetail.riskFlow.avoidance')}
                  </FormLabel>
                  <FormControl>
                    <RiskAvoidanceSelect
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isRiskOutSelectDisabled}
                      maxAllowedValue={riskInCap.avoidance}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.select'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='riskOutAvoidanceJustification'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-xs'>
                    {t(
                      'hazardsPage.hazardDetail.riskFlow.justificationOptional'
                    )}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ''}
                      rows={4}
                      disabled={isGenerating}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.justificationPlaceholder',
                        'Justification...'
                      )}
                      className='resize-none text-xs'
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className='space-y-3'>
            <FormField
              control={form.control}
              name='riskOutLikelihood'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('hazardsPage.hazardDetail.riskFlow.likelihood')}
                  </FormLabel>
                  <FormControl>
                    <RiskLikelihoodSelect
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isRiskOutSelectDisabled}
                      maxAllowedValue={riskInCap.likelihood}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.select'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='riskOutLikelihoodJustification'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-xs'>
                    {t(
                      'hazardsPage.hazardDetail.riskFlow.justificationOptional'
                    )}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ''}
                      rows={4}
                      disabled={isGenerating}
                      placeholder={t(
                        'hazardsPage.hazardDetail.riskFlow.justificationPlaceholder',
                        'Justification...'
                      )}
                      className='resize-none text-xs'
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
